import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAllMintingDapps } from '../minting/hooks/useMintingDapp';
import { mintingDappAPI } from '../minting/services/mintingDappAPI';
import type { MintingDapp } from '../minting/types';
import './MintDappsList.css';
import { useI18n } from '../i18n/context';

/**
 * MintDappsList Container
 *
 * Displays a grid/list of all available minting dapps.
 * Users can browse and select a dapp to use.
 *
 * URL: /mint
 */
const MintDappsList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const registryDapps = useAllMintingDapps();
  const [apiDapps, setApiDapps] = useState<MintingDapp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch from API to get latest list
  useEffect(() => {
    const fetchDapps = async () => {
      try {
        const result = await mintingDappAPI.getAll({ limit: 100 });
        if (result.success && Array.isArray(result.data)) {
          setApiDapps(result.data);
        } else {
          setError(t('containers.mint.unableToLoad'));
        }
      } catch (err) {
        console.error('Error fetching minting dapps:', err);
        setError(t('containers.mint.unableToLoad'));
      } finally {
        setLoading(false);
      }
    };
    fetchDapps();
  }, []);

  // Ensure arrays
  const safeApiDapps = Array.isArray(apiDapps) ? apiDapps : [];
  const safeRegistryDapps = Array.isArray(registryDapps) ? registryDapps : [];

  // Use registry (includes built-in) as fallback if API fails
  const displayDapps = safeApiDapps.length > 0 ? safeApiDapps : safeRegistryDapps;

  const handleDappSelect = (dappId: string) => {
    navigate({ to: `/mint/${dappId}` });
  };

  // Filter to only public dapps (could also filter by config.requireAuth, etc.)
  const publicDapps = displayDapps.filter(d => d.type === 'external' || d.type === 'internal');

  return (
    <>
      <main className="main-content mt-1 border-radius-lg text-green">
        <div
          className="container-fluid py-4"
          style={{
            minHeight: 'calc(100vh - 56px)',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          }}
        >
          {/* Page Header */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm bg-transparent">
                <div className="card-body p-4 text-center">
                  <div
                    className="icon icon-shape icon-xs mb-3 mx-auto"
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '12px',
                      background: 'rgba(139, 92, 246, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#8b5cf6',
                      fontSize: '1.5rem',
                    }}
                  >
                    <i className="fas fa-layer-group"></i>
                  </div>
                  <h1 className="text-light mb-2">{t('containers.mint.title')}</h1>
                  <p className="text-light opacity-7 mb-0 lead">
                    {t('containers.mint.chooseApp')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="row justify-content-center">
              <div className="col-12 text-center py-5">
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-light mt-3">{t('containers.mint.loading')}</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="row justify-content-center">
              <div className="col-12 text-center py-5">
                <i className="fas fa-exclamation-triangle fa-4x text-warning mb-4"></i>
                <h4 className="text-light mb-3">{t('containers.mint.unableToLoad')}</h4>
                <p className="text-light opacity-6 mb-4">{error}</p>
                <button onClick={() => window.location.reload()} className="btn btn-primary">
                  <i className="fas fa-redo me-2"></i> {t('containers.mint.retry')}
                </button>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && publicDapps.length === 0 && (
            <div className="row justify-content-center">
              <div className="col-12 text-center py-5">
                <i className="fas fa-inbox fa-4x text-muted mb-4"></i>
                <h4 className="text-light mb-3">{t('containers.mint.noDapps')}</h4>
                <p className="text-light opacity-6 mb-4">
                  {t('containers.mint.noDappsDesc')}
                </p>
                <Link to="/" className="btn btn-primary">
                  <i className="fas fa-home me-2"></i> {t('containers.mint.goHome')}
                </Link>
              </div>
            </div>
          )}

          {/* Dapps Grid */}
          {!loading && !error && publicDapps.length > 0 && (
            <div className="row g-4">
              {publicDapps.map((dapp) => (
                <div key={dapp.id} className="col-md-6 col-lg-4">
                  <div
                    className="card border-radius-xl overflow-hidden h-100 shadow-dark cursor-pointer"
                    style={{
                      backgroundColor: 'rgba(30, 30, 46, 0.8)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    onClick={() => handleDappSelect(dapp.id)}
                  >
                    {/* Card Header */}
                    <div
                      className="card-header bg-transparent pb-3"
                      style={{
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className="icon icon-shape me-3 flex-shrink-0"
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '10px',
                            background: 'rgba(139, 92, 246, 0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#8b5cf6',
                            fontSize: '1.25rem',
                          }}
                        >
                          {dapp.icon ? (
                            <img
                              src={dapp.icon}
                              alt={dapp.name}
                              style={{ width: '28px', height: '28px', objectFit: 'contain' }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <i
                            className={`fas ${dapp.type === 'external' ? 'fa-globe' : 'fa-code'} ${
                              dapp.icon ? 'hidden' : ''
                            }`}
                          ></i>
                        </div>
                        <div className="flex-grow-1">
                          <h4 className="text-light mb-1 h6">{dapp.name}</h4>
                          {dapp.description && (
                            <p className="text-light opacity-6 small mb-1 line-clamp-2">
                              {dapp.description}
                            </p>
                          )}
                          <small className="text-light opacity-5 small">
                            {t(dapp.type === 'external' ? 'containers.mint.externalApp' : 'containers.mint.internalTool')} • {dapp.id}
                          </small>
                        </div>
                        <div className="ms-2">
                          <i className="fas fa-chevron-right text-light opacity-4"></i>
                        </div>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="card-body">
                      {/* Tags */}
                      {(() => {
                        let tagsArray: string[] = [];
                        if (Array.isArray(dapp.tags)) {
                          tagsArray = dapp.tags;
                        } else if (typeof dapp.tags === 'string') {
                          try {
                            const parsed = JSON.parse(dapp.tags);
                            if (Array.isArray(parsed)) tagsArray = parsed;
                          } catch (e) {
                            console.warn('Failed to parse tags JSON:', e);
                          }
                        }
                        return tagsArray.length > 0 && (
                          <div className="mb-3">
                            {tagsArray.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="badge bg-secondary me-1 mb-1"
                                style={{ fontSize: '0.75rem' }}
                              >
                                {tag}
                              </span>
                            ))}
                            {tagsArray.length > 3 && (
                              <span className="badge bg-secondary mb-1">+{tagsArray.length - 3}</span>
                            )}
                          </div>
                        );
                      })()}

                      {/* Author & Version */}
                      <div className="d-flex justify-content-between text-light opacity-6 small">
                        <span>{dapp.author || t('containers.mint.unknownAuthor')}</span>
                        <span>v{dapp.version || '1.0.0'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default MintDappsList;
